/**
 * INSPECTIONS — reading and writing one, a point at a time.
 *
 * SAVED PER POINT, IMMEDIATELY. A technician doing forty checks under a
 * car on a phone will lose signal, get called away, or drop the thing.
 * An inspection that only persists when somebody presses Save is an
 * inspection that gets done twice, and the second time it gets done badly.
 *
 * Every write is an upsert on (inspection_id, point_key), which the schema
 * already makes unique — so the same point marked twice corrects itself
 * rather than appearing twice.
 */

import { db } from "./client.ts";
import { REQUIRED_POINTS, SECTIONS, type Verdict } from "../domain/inspection-points.ts";

export type Item = {
  point_key: string;
  verdict: Verdict;
  note: string | null;
  measurement: number | null;
  unit: string | null;
};

export type Inspection = {
  id: string;
  job_id: string;
  vehicle_id: string | null;
  mileage: number | null;
  notes: string | null;
  started_at: string;
  finished_at: string | null;
  shared_at: string | null;
};

const ISEL = "id,job_id,vehicle_id,mileage,notes,started_at,finished_at,shared_at";
const XSEL = "point_key,verdict,note,measurement,unit";

async function accountId(): Promise<string> {
  const sb = await db();
  const { data: me } = await sb.auth.getUser();
  const { data } = await sb.from("profiles")
    .select("account_id").eq("id", me.user?.id ?? "").maybeSingle();
  const a = (data as { account_id: string } | null)?.account_id;
  if (!a) throw new Error("Cannot tell which shop you belong to. Sign in again.");
  return a;
}

/** The one for this job, started if there is not one yet. */
export async function openInspection(
  jobId: string, vehicleId: string | null,
): Promise<{ inspection: Inspection; items: Map<string, Item> }> {
  const sb = await db();

  let { data } = await sb.from("inspections").select(ISEL)
    .eq("job_id", jobId).order("started_at", { ascending: false }).limit(1).maybeSingle();

  if (!data) {
    const account_id = await accountId();
    const made = await sb.from("inspections")
      .insert({ account_id, job_id: jobId, vehicle_id: vehicleId })
      .select(ISEL).single();
    if (made.error) throw new Error(made.error.message);
    data = made.data;
  }

  const insp = data as unknown as Inspection;
  const { data: rows } = await sb.from("inspection_items")
    .select(XSEL).eq("inspection_id", insp.id);

  const items = new Map<string, Item>();
  for (const r of (rows ?? []) as unknown as Item[]) items.set(r.point_key, r);

  /**
   * EVERYTHING STARTS AS GOOD, WRITTEN ON THE FIRST LOAD.
   *
   * Anthony: "I want the inspection report to be autofilled to ok on the
   * first load only. If they have started filling it out, retain what they
   * marked. Everything untouched would then stay in the okay position even
   * if they close and reopen. I know the issues this can cause and want
   * you to do it anyway."
   *
   * So the seed is PERSISTED, once, the first time a sheet is opened. Every
   * later open reads what is there and adds nothing — a point the
   * technician moved to "needs doing" stays where they put it, and one
   * they never touched stays good.
   *
   * The trade he has accepted: a sheet opened and walked away from now
   * holds a full set of good marks nobody verified. Two things limit that
   * and both already existed — an inspection is not finished until
   * somebody finishes it, and the customer sees nothing until it is
   * shared. So the record exists, but it does not yet claim anything.
   *
   * TYRES ARE NOT SEEDED. They carry a measurement, and a tread depth of
   * "good" with no number behind it would be an invented reading.
   */
  const seed = SECTIONS
    .filter((s) => !s.measured)
    .flatMap((s) => s.points)
    .filter((pt) => !items.has(pt.key));

  if (seed.length) {
    const account_id = await accountId();
    const { error } = await sb.from("inspection_items").upsert(
      seed.map((pt) => ({
        account_id, inspection_id: insp.id, point_key: pt.key,
        verdict: "good", note: null, measurement: null, unit: null,
      })),
      { onConflict: "inspection_id,point_key" });

    // A seed that did not save is not worth failing the whole screen over
    // — the sheet still works, it just starts blank. Say so in the log
    // rather than to a technician standing under a car.
    if (error) console.error("inspection: could not seed the sheet:", error.message);
    else {
      for (const pt of seed) {
        items.set(pt.key, {
          point_key: pt.key, verdict: "good", note: null,
          measurement: null, unit: null,
        });
      }
    }
  }

  return { inspection: insp, items };
}

/** One point, saved on the spot. */
export async function markPoint(
  inspectionId: string, pointKey: string,
  patch: { verdict?: Verdict; note?: string | null; measurement?: number | null; unit?: string | null },
): Promise<void> {
  const sb = await db();
  const account_id = await accountId();

  const { error } = await sb.from("inspection_items").upsert({
    account_id, inspection_id: inspectionId, point_key: pointKey,
    verdict: patch.verdict ?? "na",
    note: patch.note ?? null,
    measurement: patch.measurement ?? null,
    unit: patch.unit ?? null,
  }, { onConflict: "inspection_id,point_key" });

  if (error) throw new Error(error.message);
}

export async function setMileage(inspectionId: string, mileage: number | null): Promise<void> {
  const sb = await db();
  const { error } = await sb.from("inspections").update({ mileage }).eq("id", inspectionId);
  if (error) throw new Error(error.message);
}

export async function setNotes(inspectionId: string, notes: string): Promise<void> {
  const sb = await db();
  const { error } = await sb.from("inspections")
    .update({ notes: notes.trim() || null }).eq("id", inspectionId);
  if (error) throw new Error(error.message);
}

/**
 * WHAT IS LEFT TO DO.
 *
 * Tires are excluded, on purpose and on request. A point marked watch or
 * now with no note counts as UNFINISHED — a mark that costs the customer
 * money and does not say why is the thing an inspection exists to avoid.
 */
export function whatIsLeft(items: Map<string, Item>): { unanswered: string[]; unexplained: string[] } {
  const unanswered: string[] = [];
  const unexplained: string[] = [];
  for (const key of REQUIRED_POINTS) {
    const it = items.get(key);
    // Nothing is unanswered any more — every point starts as good. What
    // still blocks finishing is a fault with no reason on it, which is
    // the one thing an inspection must never leave out.
    if (!it) { unanswered.push(key); continue; }
    if ((it.verdict === "watch" || it.verdict === "now") && !String(it.note ?? "").trim()) {
      unexplained.push(key);
    }
  }
  return { unanswered, unexplained };
}

/**
 * Every point is already saved by the time this runs — seeded on the first
 * open, and rewritten as the technician marks things. So finishing only
 * stamps the time, which is the shop saying the sheet is done rather than
 * in progress.
 */
export async function finish(inspectionId: string): Promise<void> {
  const sb = await db();
  const { error } = await sb.from("inspections")
    .update({ finished_at: new Date().toISOString() }).eq("id", inspectionId);
  if (error) throw new Error(error.message);
}

/**
 * Shown to the customer only when somebody decides it is ready. An
 * inspection that appears on their page half-done reads as a shop that
 * does not know what it is doing.
 */
export async function share(inspectionId: string, on: boolean): Promise<void> {
  const sb = await db();
  const { error } = await sb.from("inspections")
    .update({ shared_at: on ? new Date().toISOString() : null }).eq("id", inspectionId);
  if (error) throw new Error(error.message);
}
